import React, { useState, useRef } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ContactUs = () => {
  const form = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean, message: string } | null>(null);

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Replace these with your actual EmailJS credentials
    const serviceID = 'service_wd6hb09';
    const templateID = 'template_dc79mkp';
    const publicKey = 'uMzKBtkK2oUz1PpTm';

    if (form.current) {
      emailjs.sendForm(serviceID, templateID, form.current, publicKey)
        .then((result) => {
          console.log(result.text);
          setSubmitStatus({
            success: true,
            message: 'Message sent successfully!'
          });
          if (form.current) {
            form.current.reset();
          }
        }, (error) => {
          console.log(error.text);
          setSubmitStatus({
            success: false,
            message: 'Failed to send message. Please try again later.'
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <section className="py-4 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header and other content remains the same */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
            {submitStatus && (
              <div className={`mb-6 p-4 rounded-lg ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {submitStatus.message}
              </div>
            )}
            <form ref={form} onSubmit={sendEmail} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="user_name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="user_name"
                    id="user_name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your first name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="user_lastname" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="user_lastname"
                    id="user_lastname"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your last name"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="user_email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="user_email"
                  id="user_email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <select
                  name="subject"
                  id="subject"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a topic</option>
                  <option value="partnership">Partnership Inquiry</option>
                  <option value="prayer">Prayer Request</option>
                  <option value="volunteer">Volunteer Opportunity</option>
                  <option value="donation">Donation Question</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Your message..."
                  required
                ></textarea>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 px-6 ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white font-medium rounded-lg transition-colors duration-300`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </motion.div>


          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Information</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <FaMapMarkerAlt className="text-blue-600 mt-1 mr-4 text-xl" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Our Location</h4>
                    <p className="text-gray-600">Kitale<br />Trans-Nzoia County, Kenya</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FaWhatsapp className="text-blue-600 mr-4 text-xl" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Whatsapp</h4>
                    <a href="https://wa.me/254707409123" className="text-gray-600 hover:text-blue-600 transition-colors">+254707409123</a>
                  </div>
                </div>

                <div className="flex items-center">
                  <FaEnvelope className="text-blue-600 mr-4 text-xl" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <a href="mailto:lifemissions21@gmail.com" className="text-gray-600 hover:text-blue-600 transition-colors">lifemissions21@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-center">
                  <FaClock className="text-blue-600 mr-4 text-xl" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Office Hours</h4>
                    <p className="text-gray-600">Tuesday - Sunday: 8AM - 5PM EAT</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-xl mx-auto">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Connect With Us</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="https://m.facebook.com/profile.php?id=61568641040883"
                  className="flex items-center space-x-3 bg-blue-50 hover:bg-blue-100 transition-colors duration-300 p-4 rounded-xl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebook className="text-blue-600 text-2xl" />
                  <span className="text-blue-700 font-medium">lifemissionsministry</span>
                </a>

                <a
                  href="/"
                  className="flex items-center space-x-3 bg-blue-50 hover:bg-blue-100 transition-colors duration-300 p-4 rounded-xl"
                >
                  <FaTwitter className="text-blue-600 text-2xl" />
                  <span className="text-blue-700 font-medium">Twitter</span>
                </a>

                <a
                  href="/"
                  className="flex items-center space-x-3 bg-blue-50 hover:bg-blue-100 transition-colors duration-300 p-4 rounded-xl"
                >
                  <FaInstagram className="text-pink-500 text-2xl" />
                  <span className="text-gray-700 font-medium">life.missions2000</span>
                </a>

                <a
                  href="/"
                  className="flex items-center space-x-3 bg-blue-50 hover:bg-blue-100 transition-colors duration-300 p-4 rounded-xl"
                >
                  <FaYoutube className="text-red-500 text-2xl" />
                  <span className="text-gray-700 font-medium">YouTube</span>
                </a>
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;